import React from "react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, CheckCircle, AlertCircle } from "lucide-react";

interface MemberPerformanceData {
  teacherId: number;
  teacherName: string;
  totalMeetings: number;
  attendedMeetings: number;
  attendanceRate: number;
  performance: 'excellent' | 'good' | 'fair' | 'poor';
}

interface ComplianceChartProps {
  data: MemberPerformanceData[];
}

export function ComplianceChart({ data }: ComplianceChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <div className="text-4xl mb-2">📋</div>
          <p>No compliance data available</p>
        </div>
      </div>
    );
  }

  const complianceThreshold = 80;
  const warningThreshold = 70;
  
  const compliantTeachers = data.filter(t => t.attendanceRate >= complianceThreshold);
  const warningTeachers = data.filter(t => t.attendanceRate >= warningThreshold && t.attendanceRate < complianceThreshold);
  const nonCompliantTeachers = data.filter(t => t.attendanceRate < warningThreshold);

  const overallComplianceRate = (compliantTeachers.length / data.length) * 100;

  const getComplianceStatus = (rate: number) => {
    if (rate >= complianceThreshold) return { status: 'compliant', icon: CheckCircle, color: 'text-green-600' };
    if (rate >= warningThreshold) return { status: 'warning', icon: AlertCircle, color: 'text-yellow-600' };
    return { status: 'non-compliant', icon: AlertTriangle, color: 'text-red-600' };
  };

  return (
    <div className="space-y-6">
      {/* Compliance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-center p-4 border rounded-lg">
          <CheckCircle className="w-8 h-8 mx-auto text-green-600 mb-2" />
          <div className="text-2xl font-bold text-green-600">{compliantTeachers.length}</div>
          <div className="text-sm text-muted-foreground">Compliant</div>
          <div className="text-xs text-muted-foreground">≥{complianceThreshold}%</div>
        </div>
        
        <div className="text-center p-4 border rounded-lg">
          <AlertCircle className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
          <div className="text-2xl font-bold text-yellow-600">{warningTeachers.length}</div>
          <div className="text-sm text-muted-foreground">Warning</div>
          <div className="text-xs text-muted-foreground">{warningThreshold}-{complianceThreshold}%</div>
        </div>
        
        <div className="text-center p-4 border rounded-lg">
          <AlertTriangle className="w-8 h-8 mx-auto text-red-600 mb-2" />
          <div className="text-2xl font-bold text-red-600">{nonCompliantTeachers.length}</div>
          <div className="text-sm text-muted-foreground">Non-Compliant</div>
          <div className="text-xs text-muted-foreground">&lt;{warningThreshold}%</div>
        </div>
      </div>

      {/* Overall Compliance Rate */}
      <div className="p-4 border rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="font-medium">Overall Compliance Rate</div>
          <div className="text-2xl font-bold">{Math.round(overallComplianceRate)}%</div>
        </div>
        <Progress value={overallComplianceRate} className="h-3" />
        <div className="flex justify-between text-xs text-muted-foreground mt-2">
          <span>0%</span>
          <span className="font-medium">Target: {complianceThreshold}%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Compliance Status by Member */}
      <div className="space-y-3">
        <h4 className="font-semibold">Individual Compliance Status</h4>
        {data
          .sort((a, b) => a.attendanceRate - b.attendanceRate)
          .map((teacher) => {
            const compliance = getComplianceStatus(teacher.attendanceRate);
            const IconComponent = compliance.icon;
            
            return (
              <div key={teacher.teacherId} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <IconComponent className={`w-5 h-5 ${compliance.color}`} />
                  <div>
                    <div className="font-medium">{teacher.teacherName}</div>
                    <div className="text-sm text-muted-foreground">
                      {teacher.attendedMeetings}/{teacher.totalMeetings} meetings
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge 
                    variant={
                      compliance.status === 'compliant' ? 'default' :
                      compliance.status === 'warning' ? 'secondary' : 'destructive'
                    }
                  >
                    {compliance.status.replace('-', ' ')}
                  </Badge>
                  <div className="text-lg font-bold">{teacher.attendanceRate}%</div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Compliance Actions */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold mb-3">Recommended Actions</h4>
        <div className="space-y-2 text-sm">
          {nonCompliantTeachers.length > 0 && (
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
              <div>
                <strong>Immediate Action Required:</strong> {nonCompliantTeachers.length} member(s) below {warningThreshold}% threshold
                <div className="text-muted-foreground mt-1">
                  Schedule individual meetings to address attendance issues
                </div>
              </div>
            </div>
          )}
          
          {warningTeachers.length > 0 && (
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
              <div>
                <strong>Warning Level:</strong> {warningTeachers.length} member(s) between {warningThreshold}-{complianceThreshold}%
                <div className="text-muted-foreground mt-1">
                  Send reminder notifications and monitor closely
                </div>
              </div>
            </div>
          )}
          
          {compliantTeachers.length > 0 && (
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
              <div>
                <strong>Compliant:</strong> {compliantTeachers.length} member(s) meeting or exceeding {complianceThreshold}% threshold
                <div className="text-muted-foreground mt-1">
                  Continue current engagement strategies
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
